import { SelectData } from "../../models";

export class ProjectIssuanceTransactionConstants {
  public static readonly DEFAULT_ISSUANCE_TRANSACTION = {
    areaHa: null,
    counterPartyCollaboratorUuid: null,
    counterPartyName: null,
    brokerCollaboratorUuid: null,
    brokerName: null,
    externalReference: null,
    fees: null,
    issuanceUuid: null,
    parentTransactionUuid: null,
    price: null,
    quantity: null,
    totalValue: null,
    transactionDate: null,
    type: null,
    expiryDate: null,
    rowVersion: null,
    uuid: null,
  };

  public static readonly ISSUANCE_TRANSACTION_TYPE_RESERVED = "Reserved";

  public static readonly ISSUANCE_TRANSACTION_TYPE_SOLD = "Sold";

  public static readonly ISSUANCE_TRANSACTION_TYPE_CANCELLED = "Cancelled";

  public static readonly ISSUANCE_TRANSACTION_TYPE_QUOTE = "Quote";

  public static readonly ADD_ISSUANCE_TRANSACTION_TYPE_SELECT_DATA = [
    {
      key: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_RESERVED,
      value: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_RESERVED,
    },
    {
      key: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD,
      value: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD,
    },
    {
      key: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE,
      value: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE,
    },
  ] as SelectData;

  /*
    Follow-on types a Progress transaction may take, by the type of the transaction it progresses (BR-02364).
    A quote may be taken up as a sale or held as a reservation, a reservation may only complete or fall
    through, and a sale may only be reversed. Quote never appears: a quote is not progressed into another
    quote, and only the transaction that opens a position carries an expiry.
  */
  public static readonly FOLLOW_ON_ISSUANCE_TRANSACTION_TYPES: Record<string, string[]> = {
    [ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE]: [
      ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD,
      ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_RESERVED,
      ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED,
    ],
    [ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_RESERVED]: [
      ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD,
      ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED,
    ],
    [ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD]: [
      ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED,
    ],
  };

  /*
    Options for the Progress overlay's Type field. An over-quoted issuance can no longer be taken up, so the
    only way to close it is to cancel it (BR-02842).
  */
  public static readonly getFollowOnTypeSelectData = (
    parentType: string | null | undefined,
    isBlocked = false
  ): SelectData => {
    if (isBlocked) {
      return [
        {
          key: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED,
          value: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED,
        },
      ];
    }

    const types = parentType
      ? (ProjectIssuanceTransactionConstants.FOLLOW_ON_ISSUANCE_TRANSACTION_TYPES[parentType] ?? [])
      : [];

    return types.map((type) => ({ key: type, value: type }));
  };
}

/*
  The query param naming the issuance transaction a link is about. Read by the project Inventory tab, which
  opens that transaction's issuance in the Unit transactions panel.
*/
export const ISSUANCE_TRANSACTION_DEEP_LINK_PARAM = "issuanceTransactionUuid";

/*
  The object_type values that name an issuance transaction on an API record, normalised. A quote-expiry
  notification carries the namespaced "project.issuance_transaction" while other producers spell it
  "Issuance Transaction", so both forms have to match.
*/
const ISSUANCE_TRANSACTION_OBJECT_TYPES = new Set(["issuancetransaction", "projectissuancetransaction"]);

/*
  True when an API record's object_type names an issuance transaction, whichever spelling it arrives in:
  spacing, dots, hyphens, underscores and case are all normalised away first. An unrecognised type reads as
  "not an issuance transaction" rather than throwing, so a caller gates behaviour on it safely.
*/
export const isIssuanceTransactionObjectType = (objectType: string | null | undefined): boolean =>
  ISSUANCE_TRANSACTION_OBJECT_TYPES.has((objectType ?? "").replace(/[\s._-]/g, "").toLowerCase());
